import { RecaptchaVerifier } from 'firebase/auth';

import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Timestamp } from '@angular/fire/firestore';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { IPhoneNumberUpdateForm } from '@auth/models/phone-number-update';
import { PhoneAuthService } from '@auth/services/phone-auth/phone-auth.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PhoneNumberValidator } from '@shared/classes/phone-number-validator';
import { AlertComponent } from '@shared/components/alerts/alert/alert.component';
import { PhoneNumberControlComponent } from '@shared/components/forms/phone-number-control/phone-number-control.component';
import { StandardControlComponent } from '@shared/components/forms/standard-control/standard-control.component';
import { IMobileNumber } from '@shared/models/numbers';
import { ToastTypes } from '@shared/models/toast';
import { ToastService } from '@shared/services/toast/toast.service';
import { UsersService } from '@user/services/users/users.service';

@Component({
	selector: 'app-update-phone-number-modal',
	imports: [CommonModule, FormsModule, ReactiveFormsModule, PhoneNumberControlComponent, StandardControlComponent, AlertComponent],
	templateUrl: './update-phone-number-modal.component.html',
	styleUrl: './update-phone-number-modal.component.scss',
})
export class UpdatePhoneNumberModalComponent implements OnInit {
	@Input() currentPhoneNumber: IMobileNumber | null = null;

	private activeModal = inject(NgbActiveModal);
	private usersService = inject(UsersService);
	private phoneAuthService = inject(PhoneAuthService);
	private auth = inject(Auth);
	private toastService = inject(ToastService);

	form: FormGroup<IPhoneNumberUpdateForm>;

	verificationCodeLoading = false;
	updatingPhoneNumber = false;
	verificationId: string | null = null;

	ngOnInit(): void {
		this.form = new FormGroup<IPhoneNumberUpdateForm>({
			phoneNumber: new FormControl<IMobileNumber | null>(this.currentPhoneNumber, [
				Validators.required,
				PhoneNumberValidator.matchDialCode(),
			]),
			verificationCode: new FormControl<string | null>({ value: null, disabled: true }, [
				Validators.required,
				Validators.minLength(6),
				Validators.maxLength(6),
			]),
		});
	}

	closeModal(): void {
		this.activeModal.close();
	}

	get phoneNumber(): FormControl<IMobileNumber | null> {
		return this.form.controls.phoneNumber;
	}

	get verificationCode(): FormControl<string | null> {
		return this.form.controls.verificationCode;
	}

	get recaptchaVerifier(): RecaptchaVerifier {
		return new RecaptchaVerifier(this.auth, 'verificationCodeButton', {
			size: 'invisible',
		});
	}

	getVerificationCode(): void {
		if (this.phoneNumber.invalid) {
			return;
		}
		this.verificationCodeLoading = true;
		this.phoneAuthService
			.requestVerificationCode(this.phoneNumber.value!, this.recaptchaVerifier)
			.then((verificationId) => {
				this.verificationId = verificationId;
			})
			.finally(() => {
				this.verificationCode.enable();
				this.phoneNumber.disable();
				this.verificationCodeLoading = false;
			});
	}

	updatehoneNumber(): void {
		if (this.verificationCode.invalid || !this.verificationId) {
			return;
		}
		this.updatingPhoneNumber = true;
		this.phoneAuthService
			.updatePhoneNumber(this.verificationId, this.verificationCode.value!)
			.then(() => {
				this.usersService.update(this.auth.currentUser!.uid, {
					phoneNumber: this.phoneNumber.value!,
					updatedAt: Timestamp.now(),
				});

				this.toastService.showToast({
					title: 'Phone Number Updated',
					message: 'Your phone number has been updated successfully.',
					type: ToastTypes.SUCCESS,
				});
				this.closeModal();
			})
			.finally(() => {
				this.updatingPhoneNumber = false;
			});
	}
}
